import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Upload, 
  Image as ImageIcon, 
  Trash2, 
  Eye,
  Grid, 
  List,
  Search,
  Lock,
  Unlock,
  Shield,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import axios from '../lib/axios';
import { toast } from 'react-hot-toast';

const ImageImportManager = () => {
  // 🔐 אבטחה מתקדמת - רק עם קוד מפתח
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const [authAttempts, setAuthAttempts] = useState(0);
  const [lockoutUntil, setLockoutUntil] = useState(null);
  
  // מצב הקומפוננטה
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' או 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  
  // רפרנסים
  const fileInputRef = useRef(null);
  
  // 🔐 קוד המפתח הסודי (רק בקוד!)
  const SECRET_ACCESS_CODE = 'SHA-IMG-2025-BEN';
  const MAX_AUTH_ATTEMPTS = 3;
  const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 דקות

  // בדיקת נעילה
  const isLockedOut = () => {
    if (!lockoutUntil) return false;
    return Date.now() < lockoutUntil;
  };

  // אימות קוד גישה
  const handleAuthenticate = () => {
    if (isLockedOut()) {
      const remainingTime = Math.ceil((lockoutUntil - Date.now()) / (1000 * 60));
      toast.error(`מערכת נעולה. נסה שוב בעוד ${remainingTime} דקות`);
      return;
    }

    if (authCode === SECRET_ACCESS_CODE) {
      setIsAuthenticated(true);
      setAuthAttempts(0);
      setLockoutUntil(null);
      toast.success('🔓 גישה מאושרת למערכת ייבוא תמונות');
      loadImages();
    } else {
      const newAttempts = authAttempts + 1;
      setAuthAttempts(newAttempts);
      
      if (newAttempts >= MAX_AUTH_ATTEMPTS) {
        setLockoutUntil(Date.now() + LOCKOUT_DURATION);
        toast.error('🔒 יותר מדי ניסיונות שגויים. המערכת נעולה ל-15 דקות');
      } else {
        toast.error(`קוד שגוי. נותרו ${MAX_AUTH_ATTEMPTS - newAttempts} ניסיונות`);
      }
      setAuthCode('');
    }
  };

  // טעינת תמונות
  const loadImages = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/images');
      setImages(response.data || []);
    } catch (error) {
      toast.error('שגיאה בטעינת תמונות');
      console.error('Error loading images:', error);
    } finally {
      setLoading(false);
    }
  };

  // העלאת תמונות
  const handleFileUpload = async (files) => {
    const fileArray = Array.from(files);
    
    for (const file of fileArray) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} אינו קובץ תמונה תקין`);
        continue;
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB
        toast.error(`${file.name} גדול מדי (מקסימום 10MB)`);
        continue;
      }

      const formData = new FormData();
      formData.append('image', file);

      try {
        setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
        
        const response = await axios.post('/admin/images/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
          }
        });

        if (response.data.success) {
          toast.success(`✅ ${file.name} הועלה בהצלחה`);
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[file.name];
            return newProgress;
          });
          loadImages(); // רענון רשימת התמונות
        }
               } catch (error) {
         console.error('Upload error:', error);
         toast.error(`❌ שגיאה בהעלאת ${file.name}`);
         setUploadProgress(prev => {
           const newProgress = { ...prev };
           delete newProgress[file.name];
           return newProgress;
         });
       }
    }
  };

  // מחיקת תמונה
  const deleteImage = async (imageId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק תמונה זו?')) return;
    
    try {
      await axios.delete(`/admin/images/${imageId}`);
      toast.success('תמונה נמחקה בהצלחה');
      loadImages();
         } catch (error) {
       console.error('Delete image error:', error);
       toast.error('שגיאה במחיקת התמונה');
     }
  };

  // מחיקה מרובה
  const deleteSelectedImages = async () => {
    if (selectedImages.length === 0) return;
    
    if (!window.confirm(`האם אתה בטוח שברצונך למחוק ${selectedImages.length} תמונות?`)) return;
    
    try {
      await axios.post('/admin/images/delete-multiple', { imageIds: selectedImages });
      toast.success(`${selectedImages.length} תמונות נמחקו בהצלחה`);
      setSelectedImages([]);
      loadImages();
         } catch (error) {
       console.error('Delete multiple images error:', error);
       toast.error('שגיאה במחיקת התמונות');
     }
  };

  // סינון תמונות
  const filteredImages = images && Array.isArray(images) ? images.filter(image => 
    image.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    image.originalName?.toLowerCase().includes(searchTerm.toLowerCase())
  ) : [];

  // בחירת תמונות
  const toggleImageSelection = (imageId) => {
    setSelectedImages(prev => 
      prev.includes(imageId) 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  // מסך אימות
  if (!isAuthenticated) {
    return (
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }}
        className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center"
      >
        <motion.div 
          initial={{ scale: 0.8, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          className="bg-gray-800 border border-red-500/20 rounded-2xl p-8 w-full max-w-md shadow-2xl"
        >
          <div className="text-center mb-6">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">מערכת ייבוא תמונות</h2>
            <p className="text-gray-400">גישה מוגבלת - נדרש קוד אימות</p>
          </div>

          {isLockedOut() && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
              <div className="flex items-center text-red-400">
                <Lock className="w-5 h-5 mr-2" />
                <span>מערכת נעולה זמנית</span>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                קוד גישה
              </label>
              <input
                type="password"
                value={authCode}
                onChange={(e) => setAuthCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAuthenticate()}
                disabled={isLockedOut()}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
                placeholder="הכנס קוד גישה..."
              />
            </div>

            <motion.button
              onClick={handleAuthenticate}
              disabled={isLockedOut() || !authCode.trim()}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
            >
              {isLockedOut() ? (
                <span className="flex items-center justify-center">
                  <Lock className="w-4 h-4 mr-2" />
                  מערכת נעולה
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Unlock className="w-4 h-4 mr-2" />
                  כניסה למערכת
                </span>
              )}
            </motion.button>

            {authAttempts > 0 && !isLockedOut() && (
              <div className="text-center text-sm text-yellow-400">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                נותרו {MAX_AUTH_ATTEMPTS - authAttempts} ניסיונות
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // מסך המערכת הראשי
  return (
    <div className="p-6 bg-gray-900 min-h-screen">
      {/* כותרת */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <ImageIcon className="w-8 h-8 text-emerald-500 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-white">מערכת ייבוא תמונות</h1>
            <p className="text-gray-400">ניהול מתקדם של תמונות המוצרים</p>
          </div>
        </div>
        
        <button
          onClick={() => {
            setIsAuthenticated(false);
            setAuthCode('');
          }}
          className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
        >
          <Lock className="w-4 h-4 mr-2" />
          יציאה
        </button>
      </div>

      {/* פעולות */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        {/* העלאת תמונות */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-gray-800 border border-gray-700 rounded-xl p-4"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => handleFileUpload(e.target.files)}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            <Upload className="w-5 h-5 mr-2" />
            העלאת תמונות
          </button>
        </motion.div>

        {/* חיפוש */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="חיפוש תמונות..."
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* תצוגה */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <div className="flex rounded-lg bg-gray-700 p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex-1 flex items-center justify-center py-2 rounded-md transition-colors ${
                viewMode === 'grid' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Grid className="w-4 h-4 mr-1" />
              רשת
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 flex items-center justify-center py-2 rounded-md transition-colors ${
                viewMode === 'list' ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <List className="w-4 h-4 mr-1" />
              רשימה
            </button>
          </div>
        </div>

        {/* פעולות על נבחרים */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          {selectedImages.length > 0 && (
            <button
              onClick={deleteSelectedImages}
              className="w-full flex items-center justify-center py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              מחק {selectedImages.length}
            </button>
          )}
        </div>
      </div>

      {/* התקדמות העלאה */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 mb-6">
          <h3 className="text-white font-medium mb-3">מעלה תמונות...</h3>
          {Object.entries(uploadProgress).map(([filename, progress]) => (
            <div key={filename} className="mb-2">
              <div className="flex justify-between text-sm text-gray-300 mb-1">
                <span>{filename}</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* רשימת/רשת תמונות */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4' : 'space-y-2'}>
          {filteredImages.map((image) => (
            <motion.div
              key={image._id}
              layout
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`relative bg-gray-800 border border-gray-700 rounded-xl overflow-hidden group ${
                viewMode === 'grid' ? 'aspect-square' : 'flex items-center p-4'
              } ${selectedImages.includes(image._id) ? 'ring-2 ring-emerald-500' : ''}`}
            >
              {/* תמונה */}
              <img
                src={image.url}
                alt={image.name}
                className={viewMode === 'grid' ? 'w-full h-full object-cover' : 'w-16 h-16 object-cover rounded-lg mr-4'}
                onError={(e) => {
                  e.target.src = '/placeholder.jpg';
                }}
              />

              {/* פרטים ופעולות */}
              <div className={`${viewMode === 'grid' ? 'absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3' : 'flex-1 flex justify-between items-center'}`}>
                <div className={viewMode === 'grid' ? 'text-white' : ''}>
                  <h4 className="font-medium truncate">{image.originalName || image.name}</h4>
                  {viewMode === 'list' && (
                    <p className="text-sm text-gray-400">{image.size && `${(image.size / 1024).toFixed(1)} KB`}</p>
                  )}
                </div>

                <div className={`flex ${viewMode === 'grid' ? 'justify-center space-x-2' : 'space-x-2'}`}>
                  <button
                    onClick={() => toggleImageSelection(image._id)}
                    className={`p-2 rounded-lg transition-colors ${
                      selectedImages.includes(image._id) 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-gray-600 hover:bg-gray-500 text-white'
                    }`}
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => window.open(image.url, '_blank')}
                    className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  
                  <button
                    onClick={() => deleteImage(image._id)}
                    className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {filteredImages.length === 0 && !loading && (
        <div className="text-center py-12">
          <ImageIcon className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-400 mb-2">אין תמונות</h3>
          <p className="text-gray-500">העלה תמונות חדשות כדי להתחיל</p>
        </div>
      )}
    </div>
  );
};

export default ImageImportManager; 